import { cloneElement, FC, isValidElement, ReactElement, ReactNode } from 'react'

import { Box, Card, Skeleton, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { optionalClickableProps } from '../../helpers/interactive'

/** Palette family that gives each KPI card its colour identity. */
export type KpiColor = 'primary' | 'secondary' | 'info' | 'warning' | 'success' | 'error'

interface KpiCardProps {
  icon: ReactElement
  label: string
  primary: string
  secondary?: ReactNode
  color: KpiColor
  /** Font size for the primary value; bumped for short hero numbers. */
  primaryFontSize?: string
  loading?: boolean
  onClick?: () => void
}

/**
 * Hero stat card for the UA Insights Overview KPI strips (game + compare).
 * Each card is colour-coded to its destination tab via a tinted icon badge and
 * a small diagonal colour-wash on a white card, then routes to that tab when
 * clicked. The badge and card lift together on hover so the whole tile reads as
 * a single interactive target. While `loading`, the primary value renders as a
 * skeleton so the strip keeps its layout before the data lands.
 */
export const KpiCard: FC<KpiCardProps> = ({ icon, label, primary, secondary, color, primaryFontSize = '1.1rem', loading = false, onClick }) => {
  const badgeIcon = isValidElement(icon) ? cloneElement(icon, { sx: { fontSize: 26, color: 'inherit' } } as Record<string, unknown>) : null

  return (
    <Card
      variant="outlined"
      {...optionalClickableProps(onClick)}
      sx={(theme) => {
        const main = theme.palette[color].main
        return {
          position: 'relative',
          overflow: 'hidden',
          px: 2.25,
          pt: 2,
          pb: 1.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          cursor: onClick ? 'pointer' : 'default',
          // White base with a small diagonal colour gradient in the top-left
          // corner so each KPI keeps a hint of its identity.
          backgroundColor: 'background.paper',
          backgroundImage: `linear-gradient(135deg, ${alpha(main, 0.07)} 0%, ${alpha(main, 0)} 40%)`,
          transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
          '& .kpi-badge': { transition: 'transform 0.2s ease, background-color 0.2s ease' },
          '&:hover': onClick
            ? {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                borderColor: alpha(main, 0.55),
                '& .kpi-badge': { transform: 'scale(1.08)', bgcolor: alpha(main, 0.2) },
              }
            : undefined,
          '&:focus-visible': onClick ? { outline: '2px solid', outlineColor: main, outlineOffset: 2 } : undefined,
        }
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.7rem', lineHeight: 1.3 }}
      >
        {label}
      </Typography>
      <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          className="kpi-badge"
          sx={(theme) => ({
            flexShrink: 0,
            width: 46,
            height: 46,
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette[color].main,
            bgcolor: alpha(theme.palette[color].main, 0.12),
          })}
        >
          {badgeIcon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <Skeleton variant="text" width="70%" sx={{ fontSize: primaryFontSize }} />
          ) : (
            <Typography
              sx={{
                fontWeight: 'bold',
                color: `${color}.main`,
                lineHeight: 1.15,
                fontSize: primaryFontSize,
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {primary}
            </Typography>
          )}
          {secondary && !loading && <Box sx={{ mt: 0.25 }}>{secondary}</Box>}
        </Box>
      </Box>
    </Card>
  )
}
